import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  MenuItem,
  InputAdornment,
  Stack,
  TableContainer,
  Paper,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import PendingProfileDeleteDialog from '../components/PendingProfileDeleteDialog';
import DeleteForever from '@mui/icons-material/DeleteForever';

interface PendingProfileItem {
  user_id: number;
  can_delete_pending_profile?: boolean;
  profile_id?: string;
  name: string;
  email: string;
  city?: string;
  completion_percentage: number;
  photo_count: number;
  submitted_at?: string;
  review_status: string;
}

interface ProfileReviewDetail {
  user_id: number;
  profile_id?: string;
  name: string;
  email: string;
  phone: string;
  gender?: string;
  date_of_birth?: string;
  height_cm?: number;
  age?: number;
  city?: string;
  state?: string;
  religion?: string;
  caste?: string;
  sub_caste?: string;
  education?: string;
  occupation?: string;
  annual_income?: string;
  marital_status?: string;
  hobbies?: string;
  languages?: string;
  dosham_status?: string;
  about_me?: string;
  completion_percentage: number;
  review_status: string;
  submitted_at?: string;
  admin_comment?: string;
  requested_fields: string[];
  photos: string[];
  extended_profile: Record<string, any>;
  change_history?: Array<{ field: string; old_value: unknown; new_value: unknown; actor: string; changed_at: string }>;
}

const AdminProfileReviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isSuperAdmin = useAuthStore((state) => state.user?.role === 'SUPER_ADMIN');
  const [deleteTarget, setDeleteTarget] = useState<PendingProfileItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<PendingProfileItem[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [detailOpen, setDetailOpen] = useState(false);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<ProfileReviewDetail | null>(null);
  const [adminComment, setAdminComment] = useState('');
  const [requestedFieldsText, setRequestedFieldsText] = useState('');
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState<Array<{ src: string; label: string }>>([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [searchText, setSearchText] = useState(searchParams.get('search') || '');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const directUserId = searchParams.get('userId');
  const directViewOpened = useRef(false);

  const mediaOrigin = String(api.defaults.baseURL || '').replace(/\/?api\/?$/, '');

  const getPhotoSrc = (photoUrl: string) => {
    if (!photoUrl) return '';
    if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
      return photoUrl;
    }
    if (photoUrl.startsWith('/')) {
      return `${mediaOrigin}${photoUrl}`;
    }
    return `${mediaOrigin}/${photoUrl}`;
  };

  const formatFieldLabel = (key: string) => (
    key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );

  const formatFieldValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const isImageDataUrl = (value: any) => typeof value === 'string' && value.startsWith('data:image/');

  const openImageViewer = (images: Array<{ src: string; label: string }>, index: number) => {
    setViewerImages(images);
    setViewerIndex(index);
    setImageViewerOpen(true);
  };

  const parseBackendDate = (value?: string) => {
    if (!value) return null;
    const normalized = /z$|[+-]\d{2}:\d{2}$/i.test(value) ? value : `${value}Z`;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatIstDateTime = (value?: string) => {
    const parsed = parseBackendDate(value);
    if (!parsed) return '-';
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Kolkata',
    }).format(parsed);
  };

  const formatHeightAsFeetInches = (heightCm?: number) => {
    if (!heightCm) return '-';
    const totalInches = Math.round(heightCm / 2.54);
    return `${Math.floor(totalInches / 12)}'${totalInches % 12}"`;
  };

  const formatDateOnly = (value?: string) => {
    if (!value) return '-';
    const [year, month, day] = value.slice(0, 10).split('-');
    if (!year || !month || !day) return '-';
    return `${day}/${month}/${year}`;
  };

  const getStatusChipColor = (status: string): 'default' | 'warning' | 'success' | 'error' => {
    if (status === 'PENDING') return 'warning';
    if (status === 'APPROVED') return 'success';
    if (status === 'CHANGES_REQUESTED') return 'error';
    return 'default';
  };

  const fetchPendingProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/admin/profile-reviews', {
        params: {
          review_status: statusFilter,
          search: searchText.trim() || undefined,
          page: page + 1,
          page_size: rowsPerPage,
        },
      });
      const total = response.data.total || 0;
      const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
      setTotalProfiles(total);
      setItems(response.data.items || []);
      setStatusCounts(response.data.status_counts || {});
      if (page > lastPage) setPage(lastPage);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load pending profiles');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchText, statusFilter]);

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setPage(0);
  };

  const handleApprove = async (userId: number) => {
    try {
      await api.post(`/users/admin/profile-reviews/${userId}/approve`, {});
      toast.success('Profile approved successfully');
      await fetchPendingProfiles();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to approve profile');
    }
  };

  const handleViewProfile = async (userId: number) => {
    try {
      setDetailLoading(true);
      setDetailOpen(true);
      const response = await api.get(`/users/admin/profile-reviews/${userId}`);
      setSelectedDetail(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load profile details');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openRequestChangesDialog = (userId: number) => {
    setSelectedUserId(userId);
    setAdminComment('');
    setRequestedFieldsText('');
    setChangeDialogOpen(true);
  };

  const handleRequestChanges = async () => {
    if (!selectedUserId) return;
    if (!adminComment.trim()) {
      toast.error('Please add admin comment before sending back');
      return;
    }

    const requestedFields = requestedFieldsText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      await api.post(`/users/admin/profile-reviews/${selectedUserId}/request-changes`, {
        comment: adminComment,
        requested_fields: requestedFields,
      });
      toast.success('Feedback sent to user for correction');
      setChangeDialogOpen(false);
      await fetchPendingProfiles();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to send feedback');
    }
  };

  useEffect(() => {
    if (!directUserId || directViewOpened.current) return;
    const userId = Number(directUserId);
    if (!Number.isInteger(userId) || userId < 1) return;
    directViewOpened.current = true;
    void handleViewProfile(userId);
  }, [directUserId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      void fetchPendingProfiles();
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [fetchPendingProfiles]);

  useEffect(() => {
    const initialSearch = searchParams.get('search') || '';
    if (initialSearch) {
      setSearchText(initialSearch);
    }
  }, [searchParams]);

  const renderRowsSection = (title: string, rows: Array<[string, any]>) => {
    const visibleRows = rows.filter(([, value]) => formatFieldValue(value) !== '-');
    if (!visibleRows.length) return null;
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#7b0011', mb: 1.25 }}>{title}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
            {visibleRows.map(([label, value]) => (
              <Typography key={label} variant="body2"><strong>{label}:</strong> {formatFieldValue(value)}</Typography>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderExtendedSection = (title: string, keys: string[]) => {
    if (!selectedDetail) return null;
    const entries = keys
      .map((key) => [key, selectedDetail.extended_profile?.[key]] as [string, any])
      .filter(([, value]) => value !== null && value !== undefined && value !== '');
    if (!entries.length) return null;
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#7b0011', mb: 1.25 }}>{title}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
            {entries.map(([key, value]) => (
              isImageDataUrl(value) ? (
                <Box key={key}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>{formatFieldLabel(key)}</Typography>
                  <img
                    src={String(value)}
                    alt={formatFieldLabel(key)}
                    onClick={() => openImageViewer([{ src: String(value), label: formatFieldLabel(key) }], 0)}
                    style={{ width: 180, height: 120, objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd', cursor: 'zoom-in' }}
                  />
                </Box>
              ) : (
                <Typography key={key} variant="body2"><strong>{formatFieldLabel(key)}:</strong> {formatFieldValue(value)}</Typography>
              )
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const extendedSections = {
    personal: ['ekdo', 'staying_with_family', 'current_country', 'time_of_birth', 'gotra', 'mother_tongue', 'body_type', 'complexion', 'diet', 'smoke', 'drink'],
    education: ['qualification_field', 'college_name', 'college_city', 'university_name', 'qualification_description'],
    professional: ['occupation_description', 'current_company_name', 'company_type', 'work_location', 'work_time', 'work_experience', 'job_type', 'remark'],
    family: ['father_alive', 'father_name', 'father_occupation', 'father_mobile', 'mother_alive', 'mother_name', 'mother_occupation', 'mother_mobile', 'brother_count', 'brother_marriage_status', 'sister_count', 'sister_marriage_status', 'family_type', 'grandfather_name', 'grandmother_name', 'native_place'],
    address: ['permanent_address', 'permanent_country', 'state', 'city', 'pin_code', 'permanent_property_type', 'permanent_property_details', 'current_address', 'current_country_address', 'current_state', 'current_city', 'current_pin_code', 'current_property_type', 'current_property_details', 'residency_status'],
    verification: ['candidate_aadhaar_number', 'parent_aadhaar_number', 'candidate_aadhar_card', 'candidate_aadhaar_back', 'father_aadhar_card', 'parent_aadhaar_back'],
  };
  const knownExtendedKeys = Object.values(extendedSections).flat();
  const hiddenExtendedKeys = ['height_cm', 'date_of_birth'];
  const isSiblingMetadataKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '').includes('sibling');
  const otherExtendedKeys = selectedDetail
    ? Object.keys(selectedDetail.extended_profile || {}).filter((key) => !knownExtendedKeys.includes(key) && !hiddenExtendedKeys.includes(key) && !isSiblingMetadataKey(key))
    : [];

  return (
    <AdminLayout currentPage="profile-review">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Profile Review Queue
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Approve completed user profiles to show green verification tick.
        </Typography>
        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 1.5 }}>
          {[
            ['ALL', 'All Requests'],
            ['PENDING', 'Pending'],
            ['CHANGES_REQUESTED', 'Sent Back'],
            ['APPROVED', 'Approved'],
            ['INCOMPLETE', 'Incomplete'],
          ].map(([key, label]) => (
            <Card
              key={key}
              sx={{
                border: statusFilter === key ? '2px solid #E63946' : '1px solid #eee',
                cursor: 'pointer',
              }}
              onClick={() => handleStatusFilterChange(key)}
            >
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="textSecondary">{label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{statusCounts[key] || 0}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 1fr 180px' }, gap: 1.5 }}>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            size="small"
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="CHANGES_REQUESTED">Sent Back</MenuItem>
            <MenuItem value="APPROVED">Approved</MenuItem>
            <MenuItem value="INCOMPLETE">Incomplete</MenuItem>
          </TextField>
          <TextField
            label="Search by Profile ID, Name, Email, Phone"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            size="small"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                void fetchPendingProfiles();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" onClick={() => { setSearchText(''); setPage(0); }}>
            Clear
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="info">No profiles found for this filter.</Alert>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
            <Table sx={{ width: '100%', minWidth: 1080, tableLayout: { xs: 'auto', lg: 'fixed' } }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Profile ID</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>City</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell><strong>Submitted (IST)</strong></TableCell>
                  <TableCell align="right"><strong>Completion</strong></TableCell>
                  <TableCell align="center"><strong>Photos</strong></TableCell>
                  <TableCell align="right"><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.user_id}>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.profile_id || '-'}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{item.email}</TableCell>
                    <TableCell>{item.city || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.review_status.replace(/_/g, ' ')}
                        size="small"
                        color={getStatusChipColor(item.review_status)}
                        sx={{ maxWidth: '100%' }}
                      />
                    </TableCell>
                    <TableCell>{formatIstDateTime(item.submitted_at)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{item.completion_percentage}%</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{item.photo_count}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                        <Button size="small" variant="outlined" sx={{ whiteSpace: 'nowrap', minWidth: 72 }} onClick={() => handleViewProfile(item.user_id)}>
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          sx={{ whiteSpace: 'nowrap', minWidth: 98 }}
                          onClick={() => openRequestChangesDialog(item.user_id)}
                          disabled={item.review_status === 'APPROVED'}
                        >
                          Send Back
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ backgroundColor: '#2A9D8F', whiteSpace: 'nowrap', minWidth: 86 }}
                          onClick={() => handleApprove(item.user_id)}
                          disabled={item.review_status === 'APPROVED'}
                        >
                          Approve
                        </Button>
                        {isSuperAdmin && item.can_delete_pending_profile && (
                          <IconButton color="error" title="Delete profile permanently" aria-label={`Delete profile ${item.profile_id || item.user_id} permanently`} onClick={() => setDeleteTarget(item)}>
                            <DeleteForever />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalProfiles}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Profiles per page:"
            />
          </CardContent>
        </Card>
      )}

      {deleteTarget && <PendingProfileDeleteDialog key={deleteTarget.user_id} profile={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={fetchPendingProfiles} />}

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { m: { xs: 0.75, sm: 2 }, width: { xs: 'calc(100% - 12px)', sm: 'calc(100% - 32px)' }, maxHeight: { xs: 'calc(100% - 12px)', sm: 'calc(100% - 32px)' } } }}
      >
        <DialogTitle>Profile Review Details</DialogTitle>
        <DialogContent sx={{ px: { xs: 1.25, sm: 3 } }}>
          {detailLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
          ) : selectedDetail ? (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#7b0011' }}>{selectedDetail.name}</Typography>
              {renderRowsSection('Overview', [
                ['Profile ID', selectedDetail.profile_id],
                ['Submitted', `${formatIstDateTime(selectedDetail.submitted_at)} IST`],
                ['Review Status', selectedDetail.review_status],
                ['Completion', `${selectedDetail.completion_percentage}%`],
                ['Email', selectedDetail.email],
                ['Phone', selectedDetail.phone],
              ])}

              {renderRowsSection('Personal Details', [
                ['Gender', selectedDetail.gender],
                ['Age', selectedDetail.age],
                ['Date of Birth', formatDateOnly(selectedDetail.date_of_birth)],
                ['Height', formatHeightAsFeetInches(selectedDetail.height_cm)],
                ['Religion', selectedDetail.religion],
                ['Caste', selectedDetail.caste],
                ['Community', selectedDetail.sub_caste],
                ['Dosha', selectedDetail.dosham_status],
                ['Marital Status', selectedDetail.marital_status],
              ])}
              {renderExtendedSection('Personal Extended Details', extendedSections.personal)}

              {renderRowsSection('Education Details', [
                ['Qualification', selectedDetail.education],
              ])}
              {renderExtendedSection('Education Extended Details', extendedSections.education)}

              {renderRowsSection('Professional Details', [
                ['Occupation', selectedDetail.occupation],
                ['Annual Income', selectedDetail.annual_income],
                ['About', selectedDetail.about_me],
                ['Hobbies', selectedDetail.hobbies],
                ['Languages', selectedDetail.languages],
              ])}
              {renderExtendedSection('Professional Extended Details', extendedSections.professional)}
              {renderExtendedSection('Family Details', extendedSections.family)}
              {renderExtendedSection('Address Details', extendedSections.address)}
              {renderExtendedSection('Verification Details', extendedSections.verification)}

              {selectedDetail.admin_comment && (
                <Alert severity="warning">Latest admin comment: {selectedDetail.admin_comment}</Alert>
              )}

              {selectedDetail.requested_fields?.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Requested Fields</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedDetail.requested_fields.map((field) => (
                      <Chip key={field} label={field} size="small" />
                    ))}
                  </Box>
                </Box>
              )}

              <Card variant="outlined">
                <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#7b0011', mb: 1.25 }}>Uploaded Photos</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedDetail.photos.length === 0 && <Typography variant="caption">No photos</Typography>}
                  {selectedDetail.photos.map((photoUrl, idx) => {
                    const images = selectedDetail.photos.map((url, photoIndex) => ({ src: getPhotoSrc(url), label: `Profile photo ${photoIndex + 1}` }));
                    return (
                    <img
                      key={`${photoUrl}-${idx}`}
                      src={getPhotoSrc(photoUrl)}
                      alt={`Profile ${idx + 1}`}
                      onClick={() => openImageViewer(images, idx)}
                      style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd', cursor: 'zoom-in' }}
                    />
                    );
                  })}
                </Box>
                </CardContent>
              </Card>

              {renderExtendedSection('Other Details', otherExtendedKeys)}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { width: 'min(960px, calc(100vw - 32px))', height: 'min(760px, calc(100vh - 80px))', maxHeight: 'calc(100vh - 80px)' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontWeight: 800 }}>{viewerImages[viewerIndex]?.label || 'Submitted Image'}</Typography>
          <IconButton aria-label="Close image viewer" onClick={() => setImageViewerOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ position: 'relative', display: 'grid', placeItems: 'center', overflow: 'hidden', p: 1 }}>
          {viewerImages[viewerIndex] && <Box component="img" src={viewerImages[viewerIndex].src} alt={viewerImages[viewerIndex].label} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
          {viewerImages.length > 1 && <>
            <IconButton aria-label="Previous image" disabled={viewerIndex === 0} onClick={() => setViewerIndex((index) => Math.max(0, index - 1))} sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.92)' }}><ChevronLeftIcon /></IconButton>
            <IconButton aria-label="Next image" disabled={viewerIndex === viewerImages.length - 1} onClick={() => setViewerIndex((index) => Math.min(viewerImages.length - 1, index + 1))} sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.92)' }}><ChevronRightIcon /></IconButton>
          </>}
        </DialogContent>
        {viewerImages.length > 1 && <DialogActions sx={{ justifyContent: 'center', overflowX: 'auto' }}>
          {viewerImages.map((image, index) => <Box key={`${image.src}-${index}`} onClick={() => setViewerIndex(index)} sx={{ width: 56, height: 56, flex: '0 0 56px', cursor: 'pointer', border: viewerIndex === index ? '3px solid #7b0011' : '1px solid #ddd', borderRadius: 1, backgroundImage: `url(${image.src})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />)}
        </DialogActions>}
      </Dialog>

      <Dialog open={changeDialogOpen} onClose={() => setChangeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Back for Corrections</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
          <TextField
            label="Admin Comment"
            multiline
            rows={4}
            value={adminComment}
            onChange={(e) => setAdminComment(e.target.value)}
            placeholder="Explain what is missing or incorrect..."
            required
          />
          <TextField
            label="Requested Fields (comma separated)"
            value={requestedFieldsText}
            onChange={(e) => setRequestedFieldsText(e.target.value)}
            placeholder="example: mother_tongue, permanent_address, photo 2 clearer"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRequestChanges}>Send Back</Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminProfileReviewPage;
